import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { RouteComponentProps } from 'react-router';
import {
  HomeIcon,
  ProjectsIcon,
  AnalyticsIcon,
  UsersIcon
} from '@linearb/linui-common';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8]
  },
  cursor: 'pointer'
}));

const StyledCardContent = styled(CardContent)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: '2rem'
});

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  marginBottom: '1rem'
}));

const WelcomeContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  padding: '2rem',
  marginBottom: '2rem',
  textAlign: 'center'
}));

const ActionButton = styled(Button)({
  marginTop: '1rem',
  padding: '0.75rem 2rem',
  borderRadius: '8px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem'
});

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  completed?: boolean;
}

interface SeamlessOnboardingDashboardProps extends RouteComponentProps {
  // Add any Redux props here
}

const SeamlessOnboardingDashboard: React.FC<
  SeamlessOnboardingDashboardProps
> = ({
  history
  // Add any other props from Redux here
}) => {
  const onboardingSteps: OnboardingStep[] = [
    {
      id: 'setup-teams',
      title: 'Set Up Your Teams',
      description:
        'Configure your development teams and team structure to get personalized insights.',
      icon: <UsersIcon size={32} />,
      action: () => history.push('/settings/teams')
    },
    {
      id: 'connect-projects',
      title: 'Connect Your Projects',
      description:
        'Link your project management tools to track delivery and planning metrics.',
      icon: <ProjectsIcon size={32} />,
      action: () => history.push('/settings/project-management')
    },
    {
      id: 'explore-metrics',
      title: 'Explore Your Metrics',
      description:
        'View your team performance, DORA metrics, and delivery insights.',
      icon: <AnalyticsIcon size={32} />,
      action: () => history.push('/performance')
    },
    {
      id: 'complete-setup',
      title: 'Complete Setup',
      description:
        'Finish configuring LinearB to unlock all features and insights.',
      icon: <HomeIcon size={32} />,
      action: () => history.push('/settings/general')
    }
  ];

  const handleStepClick = (step: OnboardingStep) => {
    step.action();
  };

  return (
    <Box sx={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <WelcomeContainer>
        <Typography
          variant='h3'
          component='h1'
          gutterBottom
          sx={{ fontWeight: 700 }}
        >
          Welcome to LinearB
        </Typography>
        <Typography
          variant='h6'
          color='text.secondary'
          sx={{ marginBottom: '1rem' }}
        >
          Let's get you started with a seamless onboarding experience
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          Follow these steps to set up your LinearB dashboard and start gaining
          insights into your development process.
        </Typography>
      </WelcomeContainer>

      <Grid container spacing={3}>
        {onboardingSteps.map((step, index) => (
          <Grid item xs={12} sm={6} md={3} key={step.id}>
            <StyledCard onClick={() => handleStepClick(step)}>
              <StyledCardContent>
                <IconContainer>{step.icon}</IconContainer>
                <Typography
                  variant='h6'
                  component='h2'
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ flexGrow: 1 }}
                >
                  {step.description}
                </Typography>
                <ActionButton
                  variant='contained'
                  color='primary'
                  onClick={e => {
                    e.stopPropagation();
                    handleStepClick(step);
                  }}
                >
                  {index === onboardingSteps.length - 1
                    ? 'Finish Setup'
                    : 'Get Started'}
                </ActionButton>
              </StyledCardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ marginTop: '3rem', textAlign: 'center' }}>
        <Typography variant='body2' color='text.secondary'>
          Need help? Check out our{' '}
          <Button
            variant='text'
            color='primary'
            onClick={() =>
              window.open('https://linearb.helpdocs.io/', '_blank')
            }
            sx={{ textTransform: 'none', padding: 0, minWidth: 'auto' }}
          >
            documentation
          </Button>{' '}
          or contact our support team.
        </Typography>
      </Box>
    </Box>
  );
};

export default SeamlessOnboardingDashboard;
